import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RotateCcw, DollarSign, Percent, Calculator } from "lucide-react";

// Helper functions for number formatting
const formatNumberWithCommas = (value: string): string => {
  const numericValue = value.replace(/[^0-9]/g, '');
  if (!numericValue) return '';
  return parseInt(numericValue, 10).toLocaleString('en-US');
};

const parseFormattedNumber = (value: string): number => {
  const numericValue = value.replace(/[^0-9]/g, '');
  return numericValue ? parseInt(numericValue, 10) : 0;
};

interface FormData {
  salePrice: string;
  firstMortgage: string;
  secondMortgage: string;
  agentCommissionPercent: string;
  titleInsurancePercent: string;
  escrowFees: string;
  homeWarranty: string;
  transferTax: string;
  attorneyFees: string;
  propertyTaxes: string;
  hoaFees: string;
  miscCosts: string;
}

const initialFormData: FormData = {
  salePrice: "",
  firstMortgage: "",
  secondMortgage: "",
  agentCommissionPercent: "6",
  titleInsurancePercent: "0.5",
  escrowFees: "",
  homeWarranty: "",
  transferTax: "",
  attorneyFees: "",
  propertyTaxes: "",
  hoaFees: "",
  miscCosts: "",
};

const SellerNetSheetCalculator = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const handleCurrencyChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatNumberWithCommas(e.target.value);
    setFormData(prev => ({ ...prev, [field]: formatted }));
  };

  const handlePercentChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    // Allow only one decimal point
    const parts = value.split('.');
    const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
    setFormData(prev => ({ ...prev, [field]: sanitized }));
  };

  const calculations = useMemo(() => {
    const salePrice = parseFormattedNumber(formData.salePrice);
    const firstMortgage = parseFormattedNumber(formData.firstMortgage);
    const secondMortgage = parseFormattedNumber(formData.secondMortgage);
    const agentCommissionPercent = parseFloat(formData.agentCommissionPercent) || 0;
    const titleInsurancePercent = parseFloat(formData.titleInsurancePercent) || 0;
    const escrowFees = parseFormattedNumber(formData.escrowFees);
    const homeWarranty = parseFormattedNumber(formData.homeWarranty);
    const transferTax = parseFormattedNumber(formData.transferTax);
    const attorneyFees = parseFormattedNumber(formData.attorneyFees);
    const propertyTaxes = parseFormattedNumber(formData.propertyTaxes);
    const hoaFees = parseFormattedNumber(formData.hoaFees);
    const miscCosts = parseFormattedNumber(formData.miscCosts);

    const agentCommission = (salePrice * agentCommissionPercent) / 100;
    const titleInsurance = (salePrice * titleInsurancePercent) / 100;

    const totalDeductions = 
      firstMortgage +
      secondMortgage +
      agentCommission +
      titleInsurance +
      escrowFees +
      homeWarranty +
      transferTax +
      attorneyFees +
      propertyTaxes +
      hoaFees +
      miscCosts;

    const netToSeller = salePrice - totalDeductions;

    return {
      salePrice,
      agentCommission,
      titleInsurance,
      totalDeductions,
      netToSeller,
    };
  }, [formData]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  };

  return (
    <div className="space-y-6">
      {/* Sale Information */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2 text-foreground">
            <DollarSign className="h-5 w-5 text-godyspo-gold" />
            Sale Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="salePrice">Sale Price</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="salePrice"
                type="text"
                inputMode="numeric"
                value={formData.salePrice}
                onChange={handleCurrencyChange('salePrice')}
                placeholder="0"
                className="pl-7"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Encumbrances */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2 text-foreground">
            <Calculator className="h-5 w-5 text-godyspo-gold" />
            Mortgage Payoffs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firstMortgage">First Mortgage Payoff</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="firstMortgage"
                type="text"
                inputMode="numeric"
                value={formData.firstMortgage}
                onChange={handleCurrencyChange('firstMortgage')}
                placeholder="0"
                className="pl-7"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="secondMortgage">Second Mortgage Payoff</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="secondMortgage"
                type="text"
                inputMode="numeric"
                value={formData.secondMortgage}
                onChange={handleCurrencyChange('secondMortgage')}
                placeholder="0"
                className="pl-7"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Closing Costs */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2 text-foreground">
            <Percent className="h-5 w-5 text-godyspo-gold" />
            Closing Costs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Agent Commission */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="agentCommissionPercent">Agent Commission (%)</Label>
              <div className="relative">
                <Input
                  id="agentCommissionPercent"
                  type="text"
                  inputMode="decimal"
                  value={formData.agentCommissionPercent}
                  onChange={handlePercentChange('agentCommissionPercent')}
                  placeholder="6"
                  className="pr-7"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Amount</Label>
              <div className="h-10 flex items-center px-3 bg-muted rounded-md text-sm">
                {formatCurrency(calculations.agentCommission)}
              </div>
            </div>
          </div>

          {/* Title Insurance */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="titleInsurancePercent">Title Insurance (%)</Label>
              <div className="relative">
                <Input
                  id="titleInsurancePercent"
                  type="text"
                  inputMode="decimal"
                  value={formData.titleInsurancePercent}
                  onChange={handlePercentChange('titleInsurancePercent')}
                  placeholder="0.5"
                  className="pr-7"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Amount</Label>
              <div className="h-10 flex items-center px-3 bg-muted rounded-md text-sm">
                {formatCurrency(calculations.titleInsurance)}
              </div>
            </div>
          </div>

          {/* Other Fees */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="escrowFees">Escrow Fees</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="escrowFees"
                  type="text"
                  inputMode="numeric"
                  value={formData.escrowFees}
                  onChange={handleCurrencyChange('escrowFees')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="homeWarranty">Home Warranty</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="homeWarranty"
                  type="text"
                  inputMode="numeric"
                  value={formData.homeWarranty}
                  onChange={handleCurrencyChange('homeWarranty')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="transferTax">Transfer Tax / Recording</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="transferTax"
                  type="text"
                  inputMode="numeric"
                  value={formData.transferTax}
                  onChange={handleCurrencyChange('transferTax')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="attorneyFees">Attorney Fees</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="attorneyFees"
                  type="text"
                  inputMode="numeric"
                  value={formData.attorneyFees}
                  onChange={handleCurrencyChange('attorneyFees')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="propertyTaxes">Property Taxes Due</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="propertyTaxes"
                  type="text"
                  inputMode="numeric"
                  value={formData.propertyTaxes}
                  onChange={handleCurrencyChange('propertyTaxes')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hoaFees">HOA Fees / Prorations</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="hoaFees"
                  type="text"
                  inputMode="numeric"
                  value={formData.hoaFees}
                  onChange={handleCurrencyChange('hoaFees')}
                  placeholder="0"
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="miscCosts">Miscellaneous Costs</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="miscCosts"
                type="text"
                inputMode="numeric"
                value={formData.miscCosts}
                onChange={handleCurrencyChange('miscCosts')}
                placeholder="0"
                className="pl-7"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Net Proceeds Result */}
      <Card className="bg-godyspo-gold/10 border-godyspo-gold">
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Estimated Net to Seller</p>
            <p className={`text-4xl font-bold ${calculations.netToSeller >= 0 ? 'text-godyspo-gold' : 'text-destructive'}`}>
              {formatCurrency(calculations.netToSeller)}
            </p>
            <p className="text-sm text-muted-foreground">
              Total Deductions: {formatCurrency(calculations.totalDeductions)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Reset Button */}
      <Button
        variant="outline"
        onClick={handleReset}
        className="w-full"
      >
        <RotateCcw className="h-4 w-4 mr-2" />
        Reset Calculator
      </Button>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground text-center">
        This calculator provides estimates only. Actual closing costs may vary based on your location, 
        lender requirements, and other factors. Consult with a real estate professional for accurate figures.
      </p>

      {/* Get an Offer CTA */}
      <Card className="bg-godyspo-gold/5 border-godyspo-gold/30 mt-8">
        <CardContent className="pt-6 pb-6 text-center">
          <h3 className="text-xl font-bold text-foreground mb-2">
            Selling Your House?
          </h3>
          <p className="text-muted-foreground mb-4">
            Get a no-obligation cash offer for your property
          </p>
          <a href="/get-offer">
            <Button className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black font-semibold px-8">
            Get Cash Offer
            </Button>
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerNetSheetCalculator;
